import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import { useMemo } from "react";

import { Loader } from "components/customElements/loader";
import { colors } from "consts";

import ToggleTimeScope from "./ToggleTimeScope";
import useFinancialData from "./useFinancialData";

const TimeSeriesChart = dynamic(
  () => import("components/AmChart/TimeSeriesChart"),
  {
    ssr: false,
  }
);

const ratiosChartConfig = {
  title: "Ratios",
  isLineValuePercent: false,
  charts: [
    {
      type: "line",
      name: "Current Ratio",
      fieldName: "current_ratio",
      color: colors.smartTeal,
    },
    {
      type: "line",
      name: "Quick Ratio",
      fieldName: "quick_ratio",
      color: colors.grass,
    },
    {
      type: "line",
      name: "Cash Ratio",
      fieldName: "cash_ratio",
      color: colors.mellowYellow,
    },
  ],
};

const leverageChartConfig = {
  title: "Leverage",
  isLineValuePercent: false,
  charts: [
    {
      type: "line",
      name: "Total Debt to Assets",
      fieldName: "totdebt_totassets",
      color: colors.smartTeal,
    },
    {
      type: "line",
      name: "Net Debt to Equity",
      fieldName: "netdebt_equity",
      color: colors.grass,
    },
    {
      type: "line",
      name: "Total Debt to Equity",
      fieldName: "totdebt_equity",
      color: colors.mellowYellow,
    },
  ],
};

const TRChartConfig = {
  title: "Turnover Ratios",
  charts: [
    {
      type: "line",
      name: "Receivables Turnover",
      fieldName: "receivables_turnover",
      color: colors.smartTeal,
    },
    {
      type: "line",
      name: "Inventory Turnover",
      fieldName: "inventory_turnover",
      color: colors.grass,
    },
    {
      type: "line",
      name: "Payables Turnover",
      fieldName: "payables_turnover",
      color: colors.mellowYellow,
    },
  ],
  isLineValuePercent: false,
};

const CCRChartConfig = {
  title: "Cash Conversion Ratios",
  charts: [
    {
      type: "line",
      name: "DSO",
      fieldName: "dso",
      color: colors.smartTeal,
    },
    {
      type: "line",
      name: "DIO",
      fieldName: "dio",
      color: colors.grass,
    },
    {
      type: "line",
      name: "DPO",
      fieldName: "dpo",
      color: colors.mellowYellow,
    },
    {
      type: "line",
      name: "Cash Conversion Cycle",
      fieldName: "cash_conversion_cycle",
      color: colors.jade,
    },
  ],
  isLineValuePercent: false,
};

interface Props {
  entity: any;
}

const Liquidity = ({ entity }: Props) => {
  const router = useRouter();
  const source = router.query.source as string;
  const version = router.query.version as string;

  const { timeScopeData: chartData, isLoading } = useFinancialData(
    entity?.slug || ""
  );

  const NDChartConfig = useMemo(
    () => ({
      title: "Net Debt",
      yAxisLabel: chartData?.[0]?.currency,
      charts: [
        {
          type: "column",
          name: "Net Debt",
          fieldName: "net_debt",
          color: colors.smartTeal,
        },
      ],
    }),
    [chartData]
  );

  const hasRatiosData = useMemo(
    () =>
      chartData?.some(
        (item) =>
          !!item?.current_ratio || !!item?.quick_ratio || !!item?.cash_ratio
      ),
    [chartData]
  );

  const hasLeverageData = useMemo(
    () =>
      chartData?.some(
        (item) =>
          !!item?.totdebt_totassets ||
          !!item?.netdebt_equity ||
          !!item?.totdebt_equity
      ),
    [chartData]
  );

  const hasNDData = useMemo(
    () => chartData?.some((item) => !!item?.net_debt),
    [chartData]
  );

  const hasTRData = useMemo(
    () =>
      chartData?.some(
        (item) =>
          !!item?.receivables_turnover ||
          !!item?.inventory_turnover ||
          !!item?.payables_turnover
      ),
    [chartData]
  );

  const hasCCRData = useMemo(
    () =>
      chartData?.some(
        (item) =>
          !!item?.receivables_turnover ||
          !!item?.inventory_turnover ||
          !!item?.payables_turnover
      ),
    [chartData]
  );

  if (isLoading) return <Loader />;

  const chartClass = "w-full h-[400px] mb-[80px]";
  return (
    <>
      <ToggleTimeScope />
      {hasRatiosData && (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={ratiosChartConfig}
            className={chartClass}
          />
        </div>
      )}
      {hasLeverageData && (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={leverageChartConfig}
            className={chartClass}
          />
        </div>
      )}
      {hasNDData && (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={NDChartConfig}
            className={chartClass}
          />
        </div>
      )}
      {(source !== "native-app" || version === "2") && hasTRData ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={TRChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
      {(source !== "native-app" || version === "2") && hasCCRData ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={CCRChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
    </>
  );
};

export default Liquidity;
